import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getColorPalettes,
    loadColorPalettes,
    persistColorPalettes,
    subscribeColorPalettes,
} from '../utils/colorPalettes';

function applyHexToColorInput(input, hex) {
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    const tracker = input._valueTracker;
    if (tracker) tracker.setValue('');
    if (setter) setter.call(input, hex);
    else input.value = hex;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Barra no rodapé do modal: começa fechada e aplica a cor armada uma única vez. */
const ColorPaletteDock = () => {
    const { t } = useTranslation('builder');
    const dockRef = useRef(null);
    const armedRef = useRef('');
    const swallowClickRef = useRef(null);
    const [data, setData] = useState(getColorPalettes);
    const [armedHex, setArmedHex] = useState('');
    const [open, setOpen] = useState(false);
    const [saveError, setSaveError] = useState('');

    useEffect(() => subscribeColorPalettes(setData), []);
    useEffect(() => {
        loadColorPalettes().catch(() => {});
    }, []);

    useEffect(() => {
        armedRef.current = armedHex;
    }, [armedHex]);

    useEffect(() => {
        const root = dockRef.current?.closest('.button-config-modal');
        if (!root) return undefined;
        const onColorTarget = (e) => {
            const input = e.target?.closest?.('input[type="color"]');
            if (e.type === 'click' && swallowClickRef.current && input === swallowClickRef.current) {
                e.preventDefault();
                e.stopPropagation();
                swallowClickRef.current = null;
                return;
            }
            const hex = armedRef.current;
            if (!hex) return;
            if (!input || !root.contains(input) || input.closest('[data-palette-source]')) return;
            e.preventDefault();
            e.stopPropagation();
            if (e.type === 'pointerdown') swallowClickRef.current = input;
            armedRef.current = '';
            setArmedHex('');
            applyHexToColorInput(input, hex);
        };
        root.addEventListener('pointerdown', onColorTarget, true);
        root.addEventListener('click', onColorTarget, true);
        return () => {
            root.removeEventListener('pointerdown', onColorTarget, true);
            root.removeEventListener('click', onColorTarget, true);
        };
    }, []);

    const palettes = data.palettes || [];
    const active = palettes.find((p) => p.id === data.activePaletteId) || palettes[0] || null;
    const colors = active?.colors || [];

    const onPickPalette = async (id) => {
        setSaveError('');
        try {
            await persistColorPalettes({ ...data, activePaletteId: id });
            armedRef.current = '';
            setArmedHex('');
        } catch {
            setSaveError(t('builder:paletteSaveError', { defaultValue: 'Não foi possível salvar.' }));
        }
    };

    const onSwatch = (hex) => {
        setArmedHex((cur) => (cur === hex ? '' : hex));
    };

    const toggleOpen = () => {
        if (open) {
            armedRef.current = '';
            setArmedHex('');
        }
        setOpen((isOpen) => !isOpen);
    };

    return (
        <div className={`bc-palette-dock${open ? ' is-open' : ''}`} data-palette-source="dock" ref={dockRef}>
            {open && (
                <div className="bc-palette-dock-panel">
                    {palettes.length > 0 && (
                        <div className="bc-palette-dock-head">
                            <select
                                className="bc-input bc-palette-dock-select"
                                value={active?.id || ''}
                                onChange={(e) => onPickPalette(e.target.value)}
                                aria-label={t('builder:myPalettesTitle', { defaultValue: 'Minhas paletas' })}
                            >
                                {palettes.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {colors.length > 0 ? (
                        <div className="bc-palette-swatches">
                            {colors.map((color) => (
                                <button
                                    key={color.id}
                                    type="button"
                                    className={`bc-palette-swatch${armedHex === color.hex ? ' armed' : ''}`}
                                    style={{ backgroundColor: color.hex }}
                                    title={color.name ? `${color.name} ${color.hex}` : color.hex}
                                    onClick={() => onSwatch(color.hex)}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="bc-palette-dock-hint">
                            {t('builder:paletteDockEmpty', { defaultValue: 'Crie uma paleta em Avançado → Configurações globais → Cor.' })}
                        </p>
                    )}
                    {colors.length > 0 && (
                        <p className="bc-palette-dock-hint">
                            {armedHex
                                ? t('builder:paletteDockArmed', { defaultValue: 'Cor pronta. Clique num quadrado de cor para aplicar. A seleção é solta depois de aplicar.' })
                                : t('builder:paletteDockHint', { defaultValue: 'Clique numa cor e depois no quadrado de cor do elemento.' })}
                        </p>
                    )}
                    {saveError ? <p className="bc-palette-dock-hint bc-palette-error">{saveError}</p> : null}
                </div>
            )}
            <button
                type="button"
                className="bc-palette-dock-toggle"
                aria-expanded={open}
                onClick={toggleOpen}
            >
                <span className="bc-palette-dock-title">{t('builder:paletteDockTitle', { defaultValue: 'Minhas cores' })}</span>
                {armedHex ? <span className="bc-palette-dock-armed-dot" style={{ backgroundColor: armedHex }} aria-hidden /> : null}
                <span className={`bc-palette-dock-chevron${open ? ' open' : ''}`} aria-hidden>▲</span>
            </button>
        </div>
    );
};

export default ColorPaletteDock;
